// The online entry point: owns the one Connection for as long as the tab is open, and routes every
// incoming ServerMessage to whichever screen is currently showing — the lobby, or a running match — sw
// itching between them on `matchStart` and on `matchEnd`/`roomClosed`. Nothing else needs to know a
// WebSocket exists; the lobby and the match view each only ever call `connection.send(...)`.
import type * as THREE from 'three';
import { createConnection, type Connection } from './connection.js';
import { browserSocket } from './socket.js';
import { renderLobby } from './lobbyView.js';
import { startOnlineMatch } from './onlineMatch.js';
import { createSound } from '../sound.js';
import type { createEngraver } from '../engrave.js';
import type { buildMap } from '../mapView.js';
import type { ClientSocket } from './socket.js';
import { t } from '../i18n/index.js';
import type { HeroId, MatchRosterEntry, RoomId, RoomState, ServerMessage } from '../../shared/protocol.js';

/* Reconnecting mid-match: the server never resends `matchStart` on `hello` (only the current
   `roomState`), so a `roomState` with a non-null `yourAckSeq` — set exactly when phase is 'match' and
   you hold a seat, see room.ts's toState() — is the only signal this ever gets that a match is already
   running. `seed` matters: net/predictedGame.ts builds its shadow Game from it, and core orders each
   tick's team updates by it (protocol.ts's RoomState) — so the room's own is used, and a server too old
   to send one leaves the prediction resolving collisions in the wrong order on half the ticks. */
function matchStartFromRoomState(room: RoomState): Extract<ServerMessage, { type: 'matchStart' }> {
  const roster: MatchRosterEntry[] = room.seats.map(s => ({ seat: s.index, team: s.team, hero: s.hero! }));
  return { type: 'matchStart', roomId: room.id, seed: room.seed ?? 0, roster, yourHero: room.yourSeat as HeroId | null, countdown: room.countdown ?? 0 };
}

export type OnlineEntryDeps = {
  scene: THREE.Scene; camera: THREE.Camera; eng: ReturnType<typeof createEngraver>;
  stageEl: HTMLElement; legendEl: HTMLElement | null; heroSelectEl: HTMLElement;
  hudEl: HTMLElement; canvas: HTMLCanvasElement; mapView: ReturnType<typeof buildMap>;
  params: URLSearchParams;
  /** Resolved before the lobby (menu or name entry, frontScreens.ts), already normalized. Sent to the server on every
      `hello` (see connection.ts's `name` option) and also used locally to fill in the one name this
      client can always speak for truthfully before the server's own `roomState` echoes it back:
      the viewer's own seat, in lobbyView.ts. */
  playerName: string;
  /** A friend's invite link (main.ts's `?room=<id>`, already format-validated there) — joined once the
      opening `listRooms` has answered, instead of just showing the room list. `null`/omitted is the
      ordinary lobby entry (menu → «Играть онлайн»). main.ts clears its own copy the instant it's read
      into this call, but that alone is not enough: `showLobby` below (this module's OWN closure) runs
      again after every match — see its own one-shot handling of this value. */
  roomId?: RoomId | null;
  /** Overrides the real WebSocket — test/client/net/entry.test.ts drives this whole module (real DOM
      calls and all) against a hand-built socket instead, the same seam connection.ts's own `connect`
      option already uses. */
  connect?(): ClientSocket;
  /** The room list's «В меню» / Esc. */
  onExit?(): void;
};

// The deployed server serves this page itself, so the socket belongs on the page's OWN origin — behind a
// proxy the public port is 443 or 80, and a hardcoded :8787 would point the socket at a port nothing
// listens on publicly. The one case where page and server genuinely differ is scripts/dev.ts, which
// serves dist/ on its own port while the game server runs separately; `?port=` overrides for anything else.
const DEV_STATIC_PORT = '4173';  // scripts/dev.ts's default — see its own PORT
const DEV_SERVER_PORT = '8787';

function wsUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const override = new URLSearchParams(location.search).get('port');
  const host = override ? `${location.hostname}:${override}`
    : location.port === DEV_STATIC_PORT ? `${location.hostname}:${DEV_SERVER_PORT}`
    : location.host;
  return `${proto}//${host}/`;
}

export function startOnline(deps: OnlineEntryDeps) {
  const { scene, camera, eng, stageEl, legendEl, heroSelectEl, hudEl, canvas, mapView, params, playerName, roomId, connect, onExit } = deps;
  heroSelectEl.hidden = true;  // shared with the offline picker; the lobby's in-room picker reuses it too
  if (legendEl) legendEl.hidden = true;
  stageEl.style.visibility = 'hidden';

  const lobbyEl = document.getElementById('lobby') as HTMLElement;
  lobbyEl.hidden = false;

  let dispatch: (msg: ServerMessage) => void = () => {};
  let connection: Connection;
  let lobby: ReturnType<typeof renderLobby> | null = null;
  let match: ReturnType<typeof startOnlineMatch> | null = null;
  let terminal = false;
  // One-shot across this WHOLE tab session, not just the first call: `showLobby` (below) is also
  // `onEnded` for a match, so a "play again" loop (no reload) would otherwise re-send `joinRoom` for a
  // friend's link after every single match — and the room it names is long gone by then (room.ts destroys
  // it at match end), so every match would end with a spurious "Комната не найдена" round trip.
  let pendingRoomId = roomId ?? null;
  // One AudioContext for as long as this tab plays online, not one per match: the lobby's "play again"
  // loop can start several matches without ever reloading the page, and an AudioContext is a scarce,
  // easily leaked browser resource — see net/onlineMatch.ts's OnlineMatchDeps doc comment.
  const sound = createSound(camera, stageEl);

  /* The statuses connection.ts treats as terminal (no reconnect loop follows): a stale cached bundle sent
     the wrong PROTOCOL_VERSION or was built against another map, or this tab's session token was just taken over by another tab/window
     (tokenStore.ts moved to sessionStorage specifically so this should now be rare — a token only crosses
     tabs via a reload restoring one that a second tab also somehow obtained). Either way the only real fix
     is a reload, so say that plainly instead of leaving whatever screen was showing looking merely stuck. */
  function showTerminal(title: string, message: string) {
    // A match still running under the reload screen would keep the pointer captured (virtualPointer.ts),
    // its invisible cursor clicking this screen's text. Its end must not bring the lobby back either.
    terminal = true;
    dispatch = () => {};
    match?.stop(); match = null;
    lobby?.destroy(); lobby = null;
    lobbyEl.hidden = false;
    stageEl.style.visibility = 'hidden';
    if (legendEl) legendEl.hidden = true;
    lobbyEl.innerHTML = `
      <div class="lobby-plate"><h1>${title}</h1></div>
      <div class="lobby-status">${message}</div>
      <button type="button" class="lobby-create" id="lobbyReload">${t('net.reload')}</button>`;
    lobbyEl.querySelector('#lobbyReload')!.addEventListener('click', () => location.reload());
  }

  function showLobby() {
    match = null;
    if (terminal) return;
    stageEl.style.visibility = 'hidden';
    if (legendEl) legendEl.hidden = true;
    lobbyEl.hidden = false;
    lobby = renderLobby(lobbyEl, heroSelectEl, msg => connection.send(msg), playerName, onExit);
    connection.send({ type: 'listRooms' });
    dispatch = msg => {
      // A reconnecting session whose seat is already mid-match gets its `roomState` pushed by the server
      // as part of `hello` itself (server.ts's handleHello), strictly before the server has even looked at
      // anything this client goes on to send — so if that is what `?room=<id>` walked into (a reload while
      // seated), this branch always fires before a `roomList` ever could, and clearing the pending id here
      // skips the join below entirely: there is no point joining a room now that a match already resumed.
      if (msg.type === 'matchStart') { pendingRoomId = null; return showMatch(msg); }
      if (msg.type === 'roomState') pendingRoomId = null;  // already seated somewhere (a resumed session): joining would only fail
      if (msg.type === 'roomState' && msg.room.yourAckSeq !== null) return showMatch(matchStartFromRoomState(msg.room), msg.room.yourAckSeq);
      // Deferred to the reply of this call's OWN `listRooms` above, not sent alongside it: by the ordering
      // argument in the comment above, a match-resume (if any) has already pre-empted this whole closure by
      // the time a `roomList` could reach it, so `pendingRoomId` surviving to here means we are genuinely
      // looking at the ordinary room list — and the list is then already populated by the time a failed
      // join's `error` renders next, landing the player on a populated list with the failure message
      // visible, not a flash of "nobody's playing" a background refresh would immediately overwrite anyway.
      if (msg.type === 'roomList' && pendingRoomId) {
        const target = pendingRoomId;
        pendingRoomId = null;
        connection.send({ type: 'joinRoom', roomId: target });
      }
      lobby!.handleMessage(msg);
    };
  }

  function showMatch(matchStart: Extract<ServerMessage, { type: 'matchStart' }>, resumeAckSeq: number | null = null) {
    lobby?.destroy(); lobby = null;  // closes any open in-room picker too — see lobbyView.ts's destroy()
    lobbyEl.hidden = true;
    stageEl.style.visibility = '';
    if (legendEl) legendEl.hidden = false;
    const m = startOnlineMatch({
      scene, camera, eng, stageEl, hudEl, canvas, mapView, params, sound, connection, matchStart, resumeAckSeq,
      onEnded: showLobby,
    });
    match = m;
    dispatch = msg => m.handleMessage(msg);
  }

  connection = createConnection({
    connect: connect ?? (() => browserSocket(wsUrl())),
    name: () => playerName || undefined,
    onMessage(msg) { dispatch(msg); },
    onStatus(status) {
      if (status === 'version-mismatch') showTerminal(t('net.versionTitle'), t('net.versionText'));
      else if (status === 'map-mismatch') showTerminal(t('net.mapTitle'), t('net.mapText'));
      else if (status === 'session-taken-elsewhere') showTerminal(t('net.sessionTitle'), t('net.sessionText'));
    },
  });

  showLobby();
}
